import { FormGroup } from '@angular/forms';
import { Leg } from '@approot/models';
import { optionsChangeHandler } from './leg.utilities';
import { ACCUMULATOR_MULTI_PERIOD } from '@approot/constants';

export function accumulatorSpotSettledDirectionChange(
  val: string,
  form: FormGroup,
  leg: Leg,
  wipeOptions = true
) {
  if (leg.productType !== 'accumulatorspotsettled') {
    return;
  }
  if (val === 'Buy') {
    // if direction is a buy
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierType',
      ['None', 'UpAndOut'],
      wipeOptions ? '' : null
    );
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierType',
      ['None', 'DownAndIn'],
      wipeOptions ? '' : null
    );
  } else if (val === 'Sell') {
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierType',
      ['None', 'UpAndIn'],
      wipeOptions ? '' : null
    );
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierType',
      ['None', 'DownAndOut'],
      wipeOptions ? '' : null
    );
  } else {
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierType',
      [],
      wipeOptions ? '' : null
    ); // if no direction is chosen wipe these
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierType',
      [],
      wipeOptions ? '' : null
    );
  }
}

export function accumulatorSpotSettledUpperBarrierTypeChange(
  val: string,
  form: FormGroup,
  leg: Leg
) {
  if (leg.productType !== 'accumulatorspotsettled') {
    return;
  }
  form.get('upperBarrier').setValue('');
  form.get('upperBarrierObservationType').setValue('');
  if (val === 'None') {
    form.get('upperBarrier').disable();
    form.get('upperBarrierObservationType').disable();
  } else if (val === 'UpAndIn') {
    form.get('upperBarrier').enable();
    form.get('upperBarrierObservationType').enable();
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierObservationType',
      ['European'],
      ''
    );
  } else if (val) {
    form.get('upperBarrier').enable();
    form.get('upperBarrierObservationType').enable();
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierObservationType',
      ['European', 'American'],
      ''
    );
  } else {
    optionsChangeHandler(form, leg, 'upperBarrierObservationType', [], ''); // if none chosen then wipe
  }
}

export function accumulatorSpotSettledlowerBarrierTypeChange(
  val: string,
  form: FormGroup,
  leg: Leg
): void {
  if (leg.productType !== 'accumulatorspotsettled') {
    return;
  }
  form.get('lowerBarrierObservationType').setValue('');
  form.get('lowerBarrier').setValue('');
  if (val === 'None') {
    form.get('lowerBarrier').disable();
    form.get('lowerBarrierObservationType').disable();
  } else if (val === 'DownAndIn') {
    form.get('lowerBarrier').enable();
    form.get('lowerBarrierObservationType').enable();
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierObservationType',
      ['European'],
      ''
    );
  } else if (val) {
    form.get('lowerBarrier').enable();
    form.get('lowerBarrierObservationType').enable();
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierObservationType',
      ['European', 'American'],
      ''
    );
  } else {
    optionsChangeHandler(form, leg, 'lowerBarrierObservationType', [], ''); // if none chose then wipe
  }
}

export function accumulatorDirectionChange(
  val: string,
  form: FormGroup,
  leg: Leg,
  wipeOptions = true
): void {
  if (leg.productType !== 'accumulator') {
    return;
  }
  if (val) {
    if (val === 'Sell') {
      optionsChangeHandler(form, leg, 'lowerBarrierType', [
        'None',
        'Resurrecting',
        'Extinguishing Discrete',
        'Extinguishing Continuous',
        'Absolute Knock-Out'
      ]);

      optionsChangeHandler(
        form,
        leg,
        'upperBarrierType',
        ['None', 'UpAndIn'],
        wipeOptions ? '' : null
      );
    } else if (val === 'Buy') {
      optionsChangeHandler(
        form,
        leg,
        'upperBarrierType',
        [
          'None',
          'Resurrecting',
          'Extinguishing Discrete',
          'Extinguishing Continuous',
          'Absolute Knock-Out'
        ],
        wipeOptions ? '' : null
      );

      optionsChangeHandler(
        form,
        leg,
        'lowerBarrierType',
        ['None', 'DownAndIn'],
        wipeOptions ? '' : null
      );
    }
  }
}

export function accumulatorBarrierObsType(
  val: string,
  barrierObsType: string,
  barrier: string,
  form: FormGroup,
  leg: Leg
) {
  if (leg.productType !== 'accumulator') {
    return;
  }
  form.get(barrierObsType).enable();
  form.get(barrier).enable();
  if (val) {
    if (val === 'None') {
      optionsChangeHandler(form, leg, barrierObsType, [], '');
      form.get(barrierObsType).disable();
      form.get(barrier).disable();
    } else if (val === 'Resurrecting' || val === 'Extinguishing Discrete') {
      optionsChangeHandler(form, leg, barrierObsType, ['Discrete'], '');
    } else if (
      val === 'Extinguishing Continuous' ||
      val === 'Absolute Knock-Out'
    ) {
      optionsChangeHandler(form, leg, barrierObsType, ['American'], '');
    } else if (val === 'UpAndIn' || val === 'DownAndIn') {
      optionsChangeHandler(
        form,
        leg,
        barrierObsType,
        ['European', 'American'],
        ''
      );
    }
  } else {
    optionsChangeHandler(form, leg, barrierObsType, [], ''); // if no value then wipe the barrierObsType
  }
}

export function accumulatorMultiPeriodDirectionChange(
  val: string,
  form: FormGroup,
  leg: Leg,
  presetsLoaded: boolean
): void {
  if (leg.productType !== ACCUMULATOR_MULTI_PERIOD) {
    return;
  }
  const setLowerDefault =
    presetsLoaded || !leg.preSetPosition?.lowerBarrierType;
  const setupperDefault =
    presetsLoaded || !leg.preSetPosition?.upperBarrierType;
  if (val === 'Buy') {
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierType',
      ['None', 'Knock-In'],
      setLowerDefault ? 'Knock-In' : null
    );
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierType',
      ['None', 'Knock-Out'],
      setupperDefault ? 'Knock-Out' : null
    );
  } else if (val === 'Sell') {
    optionsChangeHandler(
      form,
      leg,
      'lowerBarrierType',
      ['None', 'Knock-Out'],
      setLowerDefault ? 'Knock-Out' : null
    );
    optionsChangeHandler(
      form,
      leg,
      'upperBarrierType',
      ['None', 'Knock-In'],
      setupperDefault ? 'Knock-In' : null
    );
  } else {
    optionsChangeHandler(form, leg, 'lowerBarrierType', [], '');
    optionsChangeHandler(form, leg, 'upperBarrierType', [], '');
  }
}

export function accumulatorMultiPeriodBarrierTypeChange(
  val: string,
  barrierObsType: string,
  form: FormGroup,
  leg: Leg
): void {
  if (leg.productType !== ACCUMULATOR_MULTI_PERIOD) {
    return;
  }

  if (val === 'None') {
    form.get(barrierObsType).setValue('');
    form.get(barrierObsType).disable();
  } else {
    optionsChangeHandler(
      form,
      leg,
      barrierObsType,
      ['European', 'American'],
      ''
    );
  }
}
